import { createContext, useContext, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet } from "@tanstack/react-router";
import {
  Activity,
  ChartNoAxesCombined,
  ChevronsUpDown,
  CircleGauge,
  FlaskConical,
  PanelTop,
  Settings2,
  ShieldCheck,
} from "lucide-react";
import {
  ApiError,
  getBootstrap,
  getSites,
  signOut,
  type BootstrapResponse,
  type OrganizationSummary,
  type SiteSummary,
} from "./api";
import { SignInPage } from "./pages/SignIn";

const navItems = [
  { label: "Economics", to: "/", icon: ChartNoAxesCombined, enabled: true },
  { label: "Websites", to: "/sites", icon: PanelTop, enabled: true },
  { label: "Sessions", to: "/sessions", icon: Activity, enabled: false },
  {
    label: "Experiments",
    to: "/experiments",
    icon: FlaskConical,
    enabled: false,
  },
  { label: "Traffic & policy", to: "/policy", icon: Settings2, enabled: false },
] as const;

function YeswardMark() {
  return (
    <span className="yesward-mark" aria-hidden="true">
      <span />
      <i />
      <span />
    </span>
  );
}

interface ConsoleContextValue {
  bootstrap: BootstrapResponse;
  activeOrganization: OrganizationSummary;
  activeOrganizationId: string;
  setActiveOrganizationId: (id: string) => void;
  sites: SiteSummary[];
  sitesLoading: boolean;
}

const ConsoleContext = createContext<ConsoleContextValue | null>(null);

export function useConsoleContext() {
  const value = useContext(ConsoleContext);
  if (!value) throw new Error("Console context is unavailable");
  return value;
}

export function ConsoleShell() {
  const queryClient = useQueryClient();
  const [activeOrganizationId, setActiveOrganizationId] = useState("");
  const bootstrapQuery = useQuery({
    queryKey: ["bootstrap"],
    queryFn: getBootstrap,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 1,
  });
  const bootstrap = bootstrapQuery.data;

  useEffect(() => {
    if (
      bootstrap?.organizations.length &&
      !bootstrap.organizations.some(
        (organization) => organization.id === activeOrganizationId,
      )
    ) {
      setActiveOrganizationId(bootstrap.organizations[0]!.id);
    }
  }, [activeOrganizationId, bootstrap]);

  const sitesQuery = useQuery({
    queryKey: ["sites", activeOrganizationId],
    queryFn: () => getSites(activeOrganizationId),
    enabled: Boolean(activeOrganizationId),
  });
  const logoutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: async () => {
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });

  if (bootstrapQuery.isLoading) {
    return <div className="console-loader">Opening the decision room…</div>;
  }
  if (bootstrapQuery.error instanceof ApiError && bootstrapQuery.error.status === 401) {
    return <SignInPage />;
  }
  if (!bootstrap) {
    return (
      <div className="console-loader is-error">
        The control plane is unavailable. Reload to try again.
      </div>
    );
  }
  const activeOrganization = bootstrap.organizations.find(
    (organization) => organization.id === activeOrganizationId,
  );
  if (!activeOrganization) {
    return (
      <div className="console-loader is-error">
        Your account has no active client organization.
      </div>
    );
  }
  const sites = sitesQuery.data?.sites ?? [];
  const primarySite = sites[0];
  const userInitials = bootstrap.user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <ConsoleContext.Provider
      value={{
        bootstrap,
        activeOrganization,
        activeOrganizationId,
        setActiveOrganizationId,
        sites,
        sitesLoading: sitesQuery.isLoading,
      }}
    >
      <div className="console-shell">
      <aside className="sidebar">
        <div className="brand-lockup">
          <YeswardMark />
          <strong>YESWARD</strong>
        </div>

        <div className="organization-switcher">
          <span className="org-avatar">
            {activeOrganization.name.slice(0, 2).toUpperCase()}
          </span>
          <span>
            <b>{activeOrganization.name}</b>
            <small>
              {activeOrganization.accessSource} · {activeOrganization.tier} access
            </small>
          </span>
          <ChevronsUpDown size={14} aria-hidden="true" />
          <select
            aria-label="Change organization"
            value={activeOrganizationId}
            onChange={(event) => setActiveOrganizationId(event.target.value)}
          >
            {bootstrap.organizations.map((organization) => (
              <option value={organization.id} key={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
        </div>

        <nav aria-label="Primary navigation">
          <p className="nav-label">Decision room</p>
          {navItems.map((item) => {
            const Icon = item.icon;
            return item.enabled ? (
              <Link
                key={item.label}
                to={item.to}
                className="nav-link"
                activeProps={{ className: "nav-link is-active" }}
              >
                <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
                {item.label}
              </Link>
            ) : (
              <span
                className="nav-link is-disabled"
                key={item.label}
                aria-disabled="true"
              >
                <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
                {item.label}
                <small>SOON</small>
              </span>
            );
          })}
        </nav>

        <div className="sidebar-bottom">
          <div className="safety-state">
            <ShieldCheck size={17} aria-hidden="true" />
            <span>
              <b>Rendering locked</b>
              <small>Shadow gate enforced</small>
            </span>
          </div>
          <div className="account-row">
            <span className="user-dot">{userInitials}</span>
            <span>
              <b>{bootstrap.user.name}</b>
              <small>{activeOrganization.role}</small>
            </span>
            <button
              type="button"
              aria-label="Sign out"
              title="Sign out"
              onClick={() => logoutMutation.mutate()}
            >
              <CircleGauge size={15} aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      <div className="console-content">
        <header className="console-topbar">
          <div className="site-context">
            <span className="live-indicator" aria-hidden="true" />
            <span>
              <b>{primarySite?.origin?.replace(/^https?:\/\//, "") ?? activeOrganization.name}</b>
              <small>
                PRODUCTION · {primarySite?.status.toUpperCase() ?? "NO WEBSITE"}
              </small>
            </span>
            {sites.length > 1 ? <ChevronsUpDown size={14} aria-hidden="true" /> : null}
          </div>
          <div className="policy-summary">
            <span>holdout 20%</span>
            <span>AI off</span>
            <span>ceiling L0</span>
          </div>
        </header>
        <main>
          <Outlet />
        </main>
      </div>
      </div>
    </ConsoleContext.Provider>
  );
}
